import { z } from 'zod';
import { useQuery } from '@tanstack/react-query';
import { axiosClient } from '@/shared/apis/config';
import { ApiResponse } from '@/shared/types';
import { CustomQueryOptions } from '@/shared/types/global/react-query/query';
import {
  BillingCurrency,
  BillingRegion,
  MarketRegion,
  PaymentProvider,
} from '@/shared/billing';

/**
 * 공개(비로그인) region context (GET /v1.0/region/context).
 * auth-only 인 /v1.0/session/context 와 달리 로그인 없이 호출 가능하며,
 * 요청 리전/통화만 담고 구독/유저 정보는 포함하지 않는다. 랜딩 등 공개 페이지의
 * 가격/문구 region 분기에 사용한다.
 */
export const regionContextResponseSchema = z.object({
  requestRegion: z.object({
    countryCode: z.string(),
    marketRegion: z.nativeEnum(MarketRegion),
  }),
  billingRegion: z.nativeEnum(BillingRegion),
  currency: z.nativeEnum(BillingCurrency),
  paymentProvider: z.nativeEnum(PaymentProvider),
});

export type RegionContext = z.infer<typeof regionContextResponseSchema>;

export const QueryKeyUseGetRegionContext = 'getRegionContext' as const;

export const getRegionContextApi = async () => {
  const response = await axiosClient.get<ApiResponse<RegionContext>>(
    '/v1.0/region/context',
  );
  return response.data;
};

export const useGetRegionContextApi = (
  queryOptions?: CustomQueryOptions<ApiResponse<RegionContext>>,
) => {
  const { queryKey, ...rest } = queryOptions ?? {};
  return useQuery({
    queryKey: [QueryKeyUseGetRegionContext, ...(queryKey ?? [])],
    queryFn: getRegionContextApi,
    // 공개 엔드포인트라 인증 가드 없이 항상 호출 가능. 요청 리전은 자주 바뀌지 않아 캐시한다.
    staleTime: 5 * 60 * 1000,
    ...rest,
  });
};

/** region context 데이터만 반환하는 편의 훅 (없으면 undefined). */
export const useRegionContext = (): RegionContext | undefined => {
  const { data } = useGetRegionContextApi();
  return data?.data;
};

/**
 * 요청이 국외(GLOBAL) 마켓인지 여부. 약관·문의 창구처럼 화면 언어가 아니라
 * 요청 IP 로 갈려야 하는 분기의 단일 진입점이다.
 * region context 수신 전에는 false(국내)로 본다 — 기본값은 국내판이다.
 */
export const useIsGlobalMarketRegion = (): boolean =>
  useRegionContext()?.requestRegion.marketRegion === MarketRegion.GLOBAL;
